import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    signal,
    viewChild,
} from '@angular/core';
import type { ElementRef } from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { injectSupportsHoverInteraction } from '@quipex/core/utils';
import {
    injectPopoverWithHover,
    QpxButton,
    QpxHeroIcon,
    QpxIcon,
    QpxPill,
} from '@quipex/shared/ui';
import type { FocusedBuildingMetadataItem } from '../models';
import type { FocusedBuildingPreviewFactPopover } from './focused-building-preview-fact-popover/focused-building-preview-fact-popover';
import { toPreviewFactSourcePresentation } from './mapper';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'div[qpx-focused-building-preview-fact]',
    imports: [QpxButton, QpxHeroIcon, QpxIcon, NgIcon, QpxPill],
    templateUrl: './focused-building-preview-fact.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'metadata-item flex min-w-0 min-h-16 items-center gap-3 p-3',
        '(mouseover)': 'onMouseOver($event)',
        '(mouseout)': 'onMouseOut($event)',
    },
})
export class FocusedBuildingPreviewFact {
    public readonly item = input.required<FocusedBuildingMetadataItem>();

    protected readonly source = computed(() =>
        toPreviewFactSourcePresentation(this.item())
    );
    private readonly sourceRow =
        viewChild<ElementRef<HTMLElement>>('sourceRow');
    protected readonly compactSource = signal(false);
    private readonly sourceLabel =
        viewChild<ElementRef<HTMLElement>>('sourceLabel');
    private readonly expandedSourceMeasure = viewChild<ElementRef<HTMLElement>>(
        'expandedSourceMeasure'
    );
    private readonly sourceTrigger =
        viewChild<ElementRef<HTMLButtonElement>>('sourceTrigger');
    private readonly supportsHover = injectSupportsHoverInteraction();
    private activated = false;
    private hovered = false;
    protected readonly popover =
        injectPopoverWithHover<FocusedBuildingPreviewFactPopover>({
            componentInputs: () => ({
                item: this.item(),
                source: this.source(),
                getTrigger: () => this.sourceTrigger()?.nativeElement ?? null,
                shouldFocusFirstAction: () => this.activated,
            }),
            loadComponent: async () =>
                (
                    await import('./focused-building-preview-fact-popover/focused-building-preview-fact-popover')
                ).FocusedBuildingPreviewFactPopover,
            triggerSelector: '[data-focused-preview-source-trigger]',
            overlaySelector: '[data-focused-preview-fact-popover]',
            origin: (row) => this.sourceTrigger()?.nativeElement ?? row,
            onResult: () => {
                this.activated = false;
            },
        });

    constructor() {
        afterRenderEffect((onCleanup) => {
            this.source();
            const row = this.sourceRow()?.nativeElement;
            const label = this.sourceLabel()?.nativeElement;
            const measure = this.expandedSourceMeasure()?.nativeElement;
            if (!row || !label || !measure) return;
            let frame: number | undefined;
            const update = () => {
                const width = row.getBoundingClientRect().width;
                if (!width) return;
                const gap =
                    Number.parseFloat(getComputedStyle(row).columnGap) || 0;
                const required =
                    label.getBoundingClientRect().width +
                    gap +
                    measure.getBoundingClientRect().width;
                this.compactSource.set(
                    required + (this.compactSource() ? 4 : 0) > width
                );
            };
            const schedule = () => {
                if (frame !== undefined) return;
                frame = requestAnimationFrame(() => {
                    frame = undefined;
                    update();
                });
            };
            const observer =
                typeof ResizeObserver === 'undefined'
                    ? undefined
                    : new ResizeObserver(schedule);
            observer?.observe(row);
            observer?.observe(label);
            observer?.observe(measure);
            schedule();
            onCleanup(() => {
                observer?.disconnect();
                if (frame !== undefined) cancelAnimationFrame(frame);
            });
        });
    }

    protected onMouseOver(event: MouseEvent): void {
        if (
            this.hovered ||
            !this.supportsHover() ||
            !(event.target instanceof Element) ||
            !event.target.closest(
                '[data-focused-preview-label], [data-focused-preview-source-trigger]'
            )
        )
            return;
        const row = this.sourceRow()?.nativeElement;
        if (!row) return;
        this.hovered = true;
        this.popover.enterTrigger(row);
    }

    protected onMouseOut(event: MouseEvent): void {
        const row = this.sourceRow()?.nativeElement;
        if (
            !row ||
            !(event.target instanceof Node) ||
            !row.contains(event.target) ||
            (event.relatedTarget instanceof Node &&
                row.contains(event.relatedTarget))
        )
            return;
        this.hovered = false;
        if (this.supportsHover() && !this.activated)
            this.popover.leaveTrigger(event.relatedTarget);
    }

    protected onClick(): void {
        const row = this.sourceRow()?.nativeElement;
        if (!row) return;
        if (this.popover.isOpen() && this.activated) {
            this.popover.toggle(row);
            return;
        }
        if (this.popover.isOpen()) this.popover.close();
        this.activated = true;
        this.popover.toggle(row);
    }

    protected onFocusOut(event: FocusEvent): void {
        this.popover.leaveTrigger(event.relatedTarget);
    }
}
